import React, { useEffect, useMemo, useState } from "react";
import { Group, Text } from "@mantine/core";
import { IconCircleCheck, IconCircle, IconClock, IconRefresh } from '@tabler/icons-react';
import { useObjectStore } from "../zustand/objects";
import { getEditorChromeTheme } from "../utils/editorChromeTheme";
import { CHROME_FOOTER_HEIGHT } from "../utils/uiChromeTokens";

const formatRelativeTimeLabel = (ts, nowTs = Date.now()) => {
  if (!ts) return "—";
  const diffMs = nowTs - ts;
  if (!Number.isFinite(diffMs)) return "—";
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return "Just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? "min" : "mins"} ago`;
  const diffHr = Math.round(diffMin / 60);
  return `${diffHr} ${diffHr === 1 ? "hour" : "hours"} ago`;
};

const EditorRealtimeFooter = ({ connected, lastSaveAt, lastRemoteUpdateAt }) => {
  const [now, setNow] = useState(() => Date.now());
  const viewportThemeOverride = useObjectStore((s) => s.viewportThemeOverride);
  const isDarkFooter = viewportThemeOverride === "dark";
  const theme = getEditorChromeTheme(isDarkFooter);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);

  const savedLabel = useMemo(() => formatRelativeTimeLabel(lastSaveAt, now), [lastSaveAt, now]);
  const updatedLabel = useMemo(
    () => formatRelativeTimeLabel(lastRemoteUpdateAt, now),
    [lastRemoteUpdateAt, now]
  );

  const connectedColor = connected ? "#22c55e" : theme.muted;
  const ConnectedIcon = connected ? IconCircleCheck : IconCircle;

  return (
    <Group
      gap={12}
      align="center"
      style={{
        backgroundColor: theme.bg,
        borderTop: `1px solid ${theme.border}`,
        height: CHROME_FOOTER_HEIGHT,
        paddingLeft: 12,
        paddingRight: 12,
        width: "100%",
        justifyContent: "flex-start",
        flexWrap: "nowrap",
      }}
    >
      <Group gap={4} align="center" style={{ flexWrap: "nowrap" }}>
        <ConnectedIcon size={11} color={connectedColor} />
        <Text size="xs" fw={400} c={connectedColor} lh="14px" style={{ fontSize: 11 }}>
          {connected ? "Connected" : "Disconnected"}
        </Text>
      </Group>

      {lastSaveAt ? (
        <Group gap={4} align="center" style={{ flexWrap: "nowrap" }}>
          <IconClock size={11} color={theme.muted} />
          <Text size="xs" fw={400} c="dimmed" lh="14px" style={{ fontSize: 11 }}>
            Saved {savedLabel}
          </Text>
        </Group>
      ) : null}

      {lastRemoteUpdateAt ? (
        <Group gap={4} align="center" style={{ flexWrap: "nowrap" }}>
          <IconRefresh size={11} color={theme.muted} />
          <Text size="xs" fw={400} c="dimmed" lh="14px" style={{ fontSize: 11 }}>
            Updated {updatedLabel}
          </Text>
        </Group>
      ) : null}
    </Group>
  );
};

export default EditorRealtimeFooter;
